import styles from "./css/CountPicker.module.css";
import { useTranslation } from "react-i18next";
import { CountPickerStyle } from "./CountPickerStyle";
import { useEffect, useRef, useState } from "react";
import BigButton from "./BigButton";
import { toDecimalString } from "./strings";
import ListSettingView from "./ListSettingView";
import {
  isOnSectionBasedStudyFromLocalStorage,
  setIsOnLearningSectionBasedStudyToLocalStorage,
} from "../settings/util/learningSettings";
import { useBackToClose } from "../../hooks/useBackToClose";

interface CountPickerProps {
  open: boolean;
  defaultValue: number;
  style: CountPickerStyle;
  onComplete: (value: number) => void;
  onClose: () => void;
}

export default function CountPicker(props: CountPickerProps) {
  useBackToClose(props.open, props.onClose);
  const inputRef = useRef<HTMLInputElement>(null);

  const { t } = useTranslation();
  const [currentValue, setCurrentValue] = useState<number>(0);
  const [displayText, setDisplayText] = useState("");
  const [minimum, setMinimum] = useState(0);
  const [maximum, setMaximum] = useState(0);

  const [isOnSectionBasedStudy, setIsOnSectionBasedStudy] = useState(false);

  useEffect(() => {
    if (props.open === true) {
      inputRef.current?.focus();

      const newValue = props.defaultValue;
      setCurrentValue(newValue);
      setIsOnSectionBasedStudy(isOnSectionBasedStudyFromLocalStorage());
      updateValueLimit();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open]);

  useEffect(() => {
    updateDisplayText(currentValue);
  }, [currentValue]);

  function updateValueLimit() {
    switch (props.style) {
      case CountPickerStyle.learningIntervalUnitCount:
        setMinimum(1);
        setMaximum(9999);
        break;
      case CountPickerStyle.learningGoalCount:
        setMinimum(1);
        setMaximum(9999);
        break;
    }
  }

  const handleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const replacingValue = e.target.value.replace(/[^0-9]/g, ""); // 숫자 이외 제거
    if (replacingValue === "") {
      setDisplayText("");
      return;
    }

    const numberValue = Number(replacingValue);

    let newValue = 0;
    if (numberValue < minimum) {
      newValue = minimum;
    } else if (numberValue > maximum) {
      newValue = maximum;
    } else {
      newValue = numberValue;
    }

    if (currentValue !== newValue) {
      setCurrentValue(newValue);
    } else {
      updateDisplayText(newValue);
    }
  };

  function updateDisplayText(value: number) {
    const decimalString = toDecimalString(value);
    setDisplayText(decimalString);
  }

  function complete() {
    props.onComplete(currentValue);
    props.onClose();
  }

  function onContainverViewTapped() {
    props.onClose();
  }

  function onPickerContainerViewTapped(e: React.MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
  }

  if (props.open === false) {
    return null;
  }

  return (
    <>
      <div className={styles.container} onClick={onContainverViewTapped}>
        <div
          className={styles.pickerContainer}
          onClick={onPickerContainerViewTapped}
        >
          <div className={styles.contentsContainer}>
            <div className={styles.onOffContainer}>
              {props.style === CountPickerStyle.learningIntervalUnitCount && (
                <ListSettingView
                  title={t("LearningCountSettingStudyBySectionsTitle")}
                  booleanValue={isOnSectionBasedStudy}
                  valueStyle="booleanValue"
                  handleOnBooleanValueChange={(isOn: boolean) => {
                    setIsOnSectionBasedStudy(isOn);
                    setIsOnLearningSectionBasedStudyToLocalStorage(isOn);
                  }}
                />
              )}
            </div>
            {isOnSectionBasedStudy === true && (
              <div className={styles.inputContainer}>
                <input
                  className={styles.input}
                  ref={inputRef}
                  value={displayText}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="off"
                  onChange={handleValueChange}
                />
              </div>
            )}
          </div>
          <div className={styles.buttonsContainer}>
            <BigButton
              title={t("Done")}
              variant={displayText === "" ? "disable" : "normal"}
              handleClick={() => {
                complete();
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
}
